import { API_BASE_URL } from './constants';

export type SavedStatus = 'wishing' | 'owned';

export interface SavedItemProduct {
  id: string;
  name: string | null;
  brand: string | null;
  image: string | null;
  url: string | null;
  price: number | null;
  originalPrice: number | null;
  currency: string | null;
  availability: string | null;
  isDelisted: boolean;
}

export interface SavedItem {
  id: string;
  productId: string;
  collectionIds: string[];
  status: SavedStatus;
  savedAt: string;
  purchasedAt: string | null;
  product: SavedItemProduct;
}

export interface SavedCollection {
  id: string;
  name: string;
  createdAt: string;
  itemCount: number;
}

interface SavedListResponse {
  items: SavedItem[];
  collections: SavedCollection[];
}

interface CollectionsResponse {
  collections: SavedCollection[];
}

async function savedFetch(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${API_BASE_URL}/api/account/saved${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
}

async function unwrap<T>(res: Response, action: string): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${action} failed: ${res.status} ${text}`);
  }
  return (await res.json()) as T;
}

export async function fetchSaved(query?: {
  status?: SavedStatus;
  collection?: string;
}): Promise<SavedListResponse> {
  const params = new URLSearchParams();
  if (query?.status) params.set('status', query.status);
  if (query?.collection) params.set('collection', query.collection);
  const qs = params.toString();
  const res = await savedFetch(qs ? `?${qs}` : '');
  return unwrap<SavedListResponse>(res, 'Load saved pieces');
}

export async function createSavedItem(input: {
  productId: string;
  collectionIds?: string[];
}): Promise<SavedItem> {
  const res = await savedFetch('', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return unwrap<SavedItem>(res, 'Save item');
}

export async function deleteSavedItem(productId: string): Promise<void> {
  const res = await savedFetch(`/${encodeURIComponent(productId)}`, {
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 204) {
    const text = await res.text().catch(() => '');
    throw new Error(`Unsave item failed: ${res.status} ${text}`);
  }
}

export async function updateSavedItem(
  productId: string,
  patch: { status?: SavedStatus; collectionIds?: string[] },
): Promise<SavedItem> {
  const res = await savedFetch(`/${encodeURIComponent(productId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return unwrap<SavedItem>(res, 'Update saved item');
}

export async function fetchCollections(): Promise<SavedCollection[]> {
  const res = await savedFetch('/collections');
  const data = await unwrap<CollectionsResponse>(res, 'Load collections');
  return data.collections;
}

export async function createCollection(name: string): Promise<SavedCollection> {
  const res = await savedFetch('/collections', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
  return unwrap<SavedCollection>(res, 'Create collection');
}

export async function renameCollection(
  id: string,
  name: string,
): Promise<SavedCollection> {
  const res = await savedFetch(`/collections/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
  return unwrap<SavedCollection>(res, 'Rename collection');
}

export async function deleteCollection(id: string): Promise<void> {
  const res = await savedFetch(`/collections/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 204) {
    const text = await res.text().catch(() => '');
    throw new Error(`Delete collection failed: ${res.status} ${text}`);
  }
}
